/**
 * Layers recommendation rows (assets/layers-row.js).
 *
 * Drives every [data-layers-row] root (snippets/layers-block-row.liquid): loads the Layers block once
 * the row is ~300px from the viewport, renders snippets/layers-product-card.liquid slides, then builds
 * the same carousel DY rows use (theme.Slideshow on .product-single__related). An empty or failed
 * block hides only its own row.
 *
 * window.LayersRows.mount(el) mounts a row added after page load. A bare
 * <div data-layers-row data-block-id="…" data-anchor-type="search"> works: the row markup is fetched
 * once from sections/layers-row-shell and copied in, keeping the root's own data-* attributes.
 *
 * Root attributes: data-block-id (required), data-anchor-type, data-anchor-id, data-limit (12),
 * data-cards (5), data-cards-mobile (2.5), data-controls (dots | arrows | arrows-dots),
 * data-heading (text, for bare roots), data-heading-style, data-reviews, data-final-sale.
 */

const LT = await (window.LayersTheme?.ready ||
  new Promise((resolve) => document.addEventListener('layers:ready', (e) => resolve(e.detail), { once: true })));

const ROOT_MARGIN = '300px';
let shellPromise;

/** Row markup for bare roots, from ?section_id=layers-row-shell (one request per page). */
function loadShell() {
  if (!shellPromise) {
    const url = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}?section_id=layers-row-shell`;
    shellPromise = fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Inline helpers the card relies on (window.LayersCard) don't run when copied as markup.
        // The row stylesheet sits next to the root in the shell; add it once if this page lacks it.
        doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          const href = link.getAttribute('href');
          if (href && ![...document.styleSheets].some((s) => s.href && s.href.split('?')[0].endsWith(href.split('?')[0].replace(/^https?:/, '')))) {
            document.head.appendChild(link.cloneNode());
          }
        });
        doc.querySelectorAll('script:not([src])').forEach((s) => {
          if (/window\.LayersCard\b/.test(s.textContent) && window.LayersCard) return;
          const run = document.createElement('script');
          run.textContent = s.textContent;
          document.head.appendChild(run);
        });
        return doc.querySelector('[data-layers-row]');
      })
      .catch(() => null);
  }
  return shellPromise;
}

/**
 * Resolves to the element to mount, or null. A bare root gets the shell markup on a fresh clone that
 * replaces it: when the bare div sits inside another Layers component (the /search empty state), that
 * component's Alpine has already walked it, so it can't become its own lx-data root and its $data would
 * resolve to the outer component.
 */
async function ensureMarkup(el) {
  if (el.querySelector('[data-layers-row-track]')) return el;
  const shell = await loadShell();
  if (!shell) return null;
  const root = el.cloneNode(false);
  root.classList.add(...shell.classList);
  root.setAttribute('lx-data', shell.getAttribute('lx-data'));
  root.innerHTML = shell.innerHTML;
  const heading = root.dataset.heading;
  if (heading) {
    root.querySelector('[data-layers-row-heading]').textContent = heading;
    root.querySelector('.layers-row__header').hidden = false;
  }
  root._layersRowMounted = true;
  el.replaceWith(root);
  return root;
}

function sectionOf(el) {
  return el.closest('.layers-recommendations') || el;
}

function hide(el) {
  sectionOf(el).hidden = true;
  const data = LT.alpine.Alpine.$data(el);
  if (data) data.loaded = true;
}

/** Quick-shop modals can't stay inside a Flickity cell; DY rows hoist theirs to <body> the same way. */
function hoistQuickShopModals(scope) {
  const store = (window.__dyQuickShopModalStore = window.__dyQuickShopModalStore || new Map());
  scope.querySelectorAll('.modal--quick-shop[id]').forEach((modal) => {
    const existing = store.get(modal.id);
    if (existing && existing !== modal && existing.isConnected) {
      modal.remove();
      return;
    }
    document.body.appendChild(modal);
    store.set(modal.id, modal);
  });
}

/**
 * Build the carousel before Alpine renders anything. Moving Alpine-rendered nodes destroys them: the
 * app's global Alpine and Rivo's both treat a moved node as removed and run its _x_ cleanups (the
 * instances share those properties), which deletes every lx-if clone. So Flickity is created on the
 * empty track first, the still-inert lx-for template goes inside .flickity-slider, and the cards
 * render straight into the slider; afterRender() only reloads the cells.
 */
function prepareSlider(el) {
  const track = el.querySelector('[data-layers-row-track]');
  const controls = el.dataset.controls || 'dots';
  if (!track || !window.theme || typeof window.theme.Slideshow !== 'function') return;
  el._layersSlider = new window.theme.Slideshow(track, {
    cellSelector: '.layers-slide',
    prevNextButtons: controls !== 'dots',
    pageDots: controls !== 'arrows',
    adaptiveHeight: false,
    groupCells: true,
    cellAlign: 'left',
    contain: true,
    wrapAround: false,
  });
  const slider = track.querySelector('.flickity-slider');
  const template = track.querySelector(':scope > template');
  if (slider && template) slider.appendChild(template);
}

const frames = (n) => new Promise((resolve) => {
  const step = (left) => (left ? requestAnimationFrame(() => step(left - 1)) : resolve());
  step(n);
});

/** Resolve once the track is as tall as the held skeleton height (or after 1.5s), resizing Flickity each frame. */
async function settle(el, holder) {
  const target = parseFloat(holder.style.minHeight) || 0;
  const track = el.querySelector('[data-layers-row-track]');
  const until = performance.now() + 1500;
  while (performance.now() < until) {
    if (el._layersSlider) el._layersSlider.resize();
    await frames(1);
    if (!track || track.getBoundingClientRect().height >= target - 2) return;
  }
}

function afterRender(el) {
  const flickity = el._layersSlider && el._layersSlider.slideshow;
  if (!flickity) return;
  flickity.reloadCells();
  flickity.cellChange(0, true);
}

const isMerchandise = (p) => LT.card.isMerchandise(p);

async function load(el) {
  const layers = LT.sdk();
  const blockId = el.dataset.blockId;
  if (!layers || !blockId) {
    hide(el);
    return;
  }
  const cards = parseFloat(el.dataset.cards) || 5;
  const sizes = LT.card.measuredImageSizes(el, LT.card.imageSizes({ layout: 'carousel', columns: cards }));
  const params = {
    blockId,
    attributes: LT.card.attributes,
    pagination: { page: 1, limit: parseInt(el.dataset.limit, 10) || 12 },
    responseOptions: LT.card.mediaPreloads(sizes),
  };
  if (el.dataset.anchorId) {
    params.anchorId = el.dataset.anchorId;
    if (el.dataset.anchorType === 'product') params.excludeProductIds = [el.dataset.anchorId];
  }

  const controller = layers.blocks(params);
  let result;
  try {
    result = await controller.get();
  } catch {
    result = { error: true };
  }
  controller.dispose();
  const products = (result && !result.error && result.data && result.data.products) || [];
  const items = products.filter(isMerchandise);
  if (!items.length) {
    hide(el);
    return;
  }

  // Hold the skeleton's height while the cards render and Flickity sizes its viewport (it measures
  // the cells a frame or two after they exist), so swapping skeleton for cards moves nothing.
  const holder = el.querySelector('.product-recommendations') || el;
  holder.style.minHeight = `${Math.ceil(holder.getBoundingClientRect().height)}px`;

  const data = LT.alpine.Alpine.$data(el);
  data.items = items.map((p, i) =>
    LT.card.toViewModel(p, {
      index: i,
      loading: i < Math.ceil(cards) ? 'eager' : 'lazy',
      sizes,
      reviews: el.hasAttribute('data-reviews'),
      finalSale: el.hasAttribute('data-final-sale'),
    }),
  );
  data.loaded = true;
  await LT.alpine.Alpine.nextTick();
  await frames(2);

  afterRender(el);
  hoistQuickShopModals(el);
  LT.card.refresh(el);
  await settle(el, holder);
  holder.style.minHeight = '';
  // Late growth inside the cards (colorway rows, Yotpo stars) changes cell heights after Flickity measured them.
  if (el._layersSlider && window.ResizeObserver) {
    let timer;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => el._layersSlider && el._layersSlider.resize && el._layersSlider.resize(), 120);
    });
    el.querySelectorAll('.layers-slide').forEach((cell) => ro.observe(cell));
    el._layersResize = ro;
  }
  el.dispatchEvent(new CustomEvent('layers:row:loaded', { bubbles: true, detail: { blockId, count: items.length } }));
}

async function mount(el) {
  if (!el || el._layersRowMounted) return;
  el._layersRowMounted = true;
  const root = await ensureMarkup(el);
  if (!root) {
    el.hidden = true;
    return;
  }
  el = root;
  // theme.Slideshow comes from theme.js (a deferred script); make sure it ran before building the slider.
  if (!(window.theme && window.theme.Slideshow) && document.readyState === 'loading') {
    await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
  }
  prepareSlider(el);
  LT.alpine.mount(el);
  if (!('IntersectionObserver' in window)) {
    load(el);
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      load(el);
    },
    { rootMargin: ROOT_MARGIN },
  );
  io.observe(el);
}

function mountAll(scope) {
  (scope || document).querySelectorAll('[data-layers-row]').forEach(mount);
}

window.LayersRows = { mount, mountAll };
mountAll(document);

// Theme editor: sections re-render in place.
document.addEventListener('shopify:section:load', (e) => mountAll(e.target));
